import { cloneDeep, isEqual } from 'lodash';
import { ProductionOrganizationInfo, ProductionOrganizationInfoInput } from 'sr-types/lib/production/v1/graphql';
import { ClassificationInput } from 'sr-types/lib/search/v1/graphql';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { Organization } from '../../organization/organization';

export const getOrganizationWithKeyFlag = (
    organizationsList: ProductionOrganizationInfo[],
    keyOrganizations: ProductionOrganizationInfo[]
): ProductionOrganizationInfoWithKey[] => {
    return organizationsList
        ? organizationsList.map((organization) => {
              return {
                  ...organization,
                  isKey:
                      Array.isArray(keyOrganizations) &&
                      keyOrganizations.some(
                          (prodOrg) => prodOrg?.organizationReference?.id === organization?.organizationReference?.id
                      ) &&
                      keyOrganizations.some((prodOrg) =>
                          isEqual(prodOrg?.organizationTypes, organization?.organizationTypes)
                      ) &&
                      keyOrganizations.some((prodOrg) =>
                          isEqual(prodOrg?.productionRoles, organization?.productionRoles)
                      )
              };
          })
        : [];
};

export const getKeyOrganizationInput = (
    organizationList: ProductionOrganizationInfoWithKey[],
    markedOrganization?: ProductionOrganizationInfoWithKey,
    unmarkedOrganization?: ProductionOrganizationInfoWithKey,
    markNewOrganization?: ProductionOrganizationInfoWithKey
): ProductionOrganizationInfoInput[] => {
    const organizations = cloneDeep(organizationList);
    if (markedOrganization) {
        organizations[
            organizationList.findIndex((org) => {
                return (
                    org?.organizationReference?.id === markedOrganization?.organizationReference?.id &&
                    isEqual(org?.productionRoles, markedOrganization?.productionRoles) &&
                    isEqual(org?.organizationTypes, markedOrganization?.organizationTypes)
                );
            })
        ].isKey = true;
    } else if (unmarkedOrganization) {
        const index = organizationList.findIndex((org) => {
            return (
                org?.organizationReference?.id === unmarkedOrganization?.organizationReference?.id &&
                isEqual(org?.productionRoles, unmarkedOrganization?.productionRoles) &&
                isEqual(org?.organizationTypes, unmarkedOrganization?.organizationTypes)
            );
        });
        organizations[index].isKey = false;
    } else if (markNewOrganization) {
        organizations.push(markNewOrganization);
    }
    return organizations
        .filter((org) => org.isKey)
        .map((org) => {
            const { name, organizationReference, organizationTypes, organizationCategories, productionRoles } = org;
            const organizationCategoriesInput = (organizationCategories as ClassificationInput[]) || [];
            return {
                name,
                organizationReference,
                organizationTypes,
                organizationCategories: organizationCategoriesInput,
                productionRoles
            };
        });
};

export type ProductionOrganizationInfoWithKey = ProductionOrganizationInfo & { isKey: boolean };

export const organizationGroupingByName = (associatedOrgs: ProductionOrganizationInfo[]) => {
    if (!associatedOrgs) return [];

    // final result array
    const result: ProductionOrganizationInfo[] = [];

    // Simple groupBy implementation
    const groupedData = associatedOrgs.reduce((acc, org) => {
        const key = org.name.trim();
        (acc[key] ||= []).push(org);
        return acc;
    }, {});

    // Convert grouped data into desired format
    for (let org in groupedData) {
        const grouped = groupedData[org];
        const combinedRole = new Set();
        const combinedTypes = new Set();
        const combinedCategories = new Set();

        grouped.forEach((gr) => {
            gr?.productionRoles && gr?.productionRoles.forEach((role) => combinedRole.add(role));
            gr?.organizationTypes && gr?.organizationTypes.forEach((type) => combinedTypes.add(type));
            gr?.organizationCategories && gr?.organizationCategories.forEach((cat) => combinedCategories.add(cat));
        });

        const final: ProductionOrganizationInfo = {
            name: org,
            organizationCategories: Array.from(combinedCategories),
            organizationReference: grouped[0]?.organizationReference,
            organizationTypes: Array.from(combinedTypes) as string[],
            productionRoles: Array.from(combinedRole) as string[],
            resourceCategory: grouped[0]?.resourceCategory || undefined
        };

        result.push(final);
    }

    return result;
};

export type OrganizationWithRoles = Organization & { organizationRole: string[] };

export const toProductOrganizationInput = (
    input: OrganizationWithRoles,
    id: string
): ProductionOrganizationInfoInput => {
    const associatedOrganizationInput: ProductionOrganizationInfoInput = {
        name: input.publicName,
        organizationCategories: input.hierarchicalCategories,
        organizationTypes: input.organizationTypes,
        productionRoles: input.organizationRole,
        organizationReference: {
            id,
            label: input.publicName,
            type: ENTITY_CONSTANTS.organization
        }
    };

    return associatedOrganizationInput;
};

export function normalizeOrganizations(organizations: ProductionOrganizationInfo[]) {
    if (!Array.isArray(organizations)) return [];
    return organizations.map((org) => ({
        ...org,
        id: org.organizationReference?.id ?? ''
    }));
}
